import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { biz, type Product } from '../../lib/business'
import { money } from '../../lib/format'
import { useToast } from '../../app/Toasts'
import { Field } from './ContactsPage'

// The rates GST actually uses. A free-text box here is how 1.8% ends up on an
// invoice, so it is a fixed list.
const GST_RATES = [0, 0.25, 3, 5, 12, 18, 28]
const UNITS = ['Nos', 'Pcs', 'Kg', 'Ltr', 'Mtr', 'Box', 'Set', 'Hrs', 'Days']

const EMPTY: Partial<Product> & { name: string } = {
  name: '', kind: 'goods', unit: 'Nos', gst_rate: 18, sale_price: 0,
}

export default function ProductsPage() {
  const { push } = useToast()
  const [rows, setRows] = useState<Product[]>([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<(Partial<Product> & { name: string }) | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try { setRows((await biz.products(q || undefined)).items) }
    catch { /* ignore */ } finally { setLoading(false) }
  }, [q])

  useEffect(() => {
    const t = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(t)
  }, [load, q])

  const save = async () => {
    if (!editing) return
    setBusy(true)
    try {
      if (editing.id) await biz.updateProduct(editing.id, editing as Product)
      else await biz.createProduct(editing)
      push('success', editing.id ? 'Product updated' : 'Product created')
      setEditing(null)
      load()
    } catch (e) {
      push('error', 'Could not save', e instanceof ApiError ? e.message : undefined)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Products &amp; services</span>
          <div className="grow" />
          <input className="input" style={{ width: 220 }}
            placeholder="Search name, SKU, HSN"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn btn-sm btn-primary" onClick={() => setEditing({ ...EMPTY })}>
            New product
          </button>
        </div>

        {loading ? (
          <div className="card-body"><div className="skeleton" style={{ height: 120 }} /></div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <div className="empty-title">Nothing here yet</div>
            <div>Add what you sell. Quotations and invoices pull price, unit,
              HSN and GST rate straight from here.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th><th>Type</th><th className="col-optional">HSN / SAC</th><th className="col-optional">Unit</th>
                  <th className="num">Sale price</th><th className="num">GST</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 520 }}>{p.name}</div>
                      {p.sku && <div className="muted mono" style={{ fontSize: 11 }}>{p.sku}</div>}
                    </td>
                    <td>
                      <span className={`pill ${p.kind === 'service' ? 'pill-accent' : 'pill-mute'}`}>
                        {p.kind}
                      </span>
                    </td>
                    <td className="mono col-optional" style={{ fontSize: 12 }}>
                      {p.hsn_sac ?? <span className="muted">—</span>}
                    </td>
                    <td className="col-optional">{p.unit}</td>
                    <td className="num">{p.sale_price != null ? money(p.sale_price) : '—'}</td>
                    <td className="num">{p.gst_rate}%</td>
                    <td className="num">
                      <button className="btn btn-sm" onClick={() => setEditing({ ...p })}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">{editing.id ? `Edit ${editing.name}` : 'New product'}</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Name" required>
                <input className="input" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </Field>
              <Field label="SKU">
                <input className="input input-mono" value={editing.sku ?? ''}
                  onChange={(e) => setEditing({ ...editing, sku: e.target.value })} />
              </Field>
              <Field label="Type">
                <select className="input" value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value as 'goods' | 'service' })}>
                  <option value="goods">Goods</option>
                  <option value="service">Service</option>
                </select>
              </Field>
              <Field label={editing.kind === 'service' ? 'SAC code' : 'HSN code'}
                     hint="4, 6 or 8 digits">
                <input className="input input-mono" value={editing.hsn_sac ?? ''}
                  onChange={(e) => setEditing({ ...editing, hsn_sac: e.target.value })} />
              </Field>
              <Field label="Unit">
                <select className="input" value={editing.unit}
                  onChange={(e) => setEditing({ ...editing, unit: e.target.value })}>
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="GST rate">
                <select className="input" value={editing.gst_rate}
                  onChange={(e) => setEditing({ ...editing, gst_rate: Number(e.target.value) })}>
                  {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
              </Field>
              <Field label="Sale price">
                <input className="input" type="number" step="0.01" value={editing.sale_price ?? 0}
                  onChange={(e) => setEditing({ ...editing, sale_price: Number(e.target.value) })} />
              </Field>
              <Field label="Purchase price">
                <input className="input" type="number" step="0.01" value={editing.purchase_price ?? 0}
                  onChange={(e) => setEditing({ ...editing, purchase_price: Number(e.target.value) })} />
              </Field>
            </div>
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={save} disabled={busy || !editing.name}>
                {busy && <span className="spinner" />}Save
              </button>
              <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
